/**
 * <byob-builder> — Build Your Own Bundle (Frøya Organics).
 *
 * Standalone asset on purpose (NOT part of the webpack build): shipping one rebuilt chunk of
 * the bundle broke the live cart once (see project memory), and this page has no dependency
 * on the bundle beyond `cart:refresh`.
 *
 * Owns every price after the first paint and re-prices the WHOLE grid on every add, remove
 * and toggle flip, because unlocking a tier changes what every card costs.
 *
 * DISPLAY ONLY: nothing here discounts anything, and nothing it writes onto a line item may
 * be trusted server-side — /cart/add.js is reachable by hand. The discount Function derives
 * the tier by counting the cart itself. Logic is identical to Norse Organics' build and to the
 * interactive artifact (byob-logic-prototype-froya.html), which wins over Figma on any number.
 */

if (!customElements.get('byob-builder')) {
  /**
   * The stepper "−" / "+" as vectors, traced off the glyphs weight 700 actually renders here (measured on the live
   * site, NOT the Bold .woff2: this theme's later @font-face rules remap the Pangram weight
   * table, so 700 is not the Bold cut)
   * (minus 370x78; plus ink 370 square, bars 86 vertical / 78 horizontal).
   *
   * NOT typed as text: PP Pangram Sans ships hhea ascent/descent 810/-230 but OS/2
   * winAscent/winDescent 1000/0 with USE_TYPO_METRICS off. Blink reads hhea on macOS and
   * the win* pair on Windows, which puts the baseline ~21% of the font size lower on
   * Windows — the glyph sank to the bottom of its button there. Geometry has no metrics,
   * so these centre identically on every platform. Mirrors snippets/byob-card.liquid.
   */
  const MINUS_SVG =
    '<svg class="byob-line__step-glyph" viewBox="0 0 370 78" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M0 39h370" stroke="currentColor" stroke-width="78"/></svg>';
  const PLUS_SVG =
    '<svg class="byob-line__step-glyph" viewBox="0 0 370 370" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M185 0v370" stroke="currentColor" stroke-width="86"/>' +
    '<path d="M0 185h370" stroke="currentColor" stroke-width="78"/></svg>';

  /**
   * .6 or above rounds up, at or below .5 rounds down. Rounded ONCE, at the unit price; line
   * totals and the bundle total are built from these already-rounded numbers so everything
   * on screen adds up. Mirrors snippets/byob-money.liquid and the Function's roundUnit().
   */
  function roundUnit(cents) {
    const value = Math.max(0, Math.floor(Number(cents) || 0));
    const whole = Math.floor(value / 100);
    return (value - whole * 100 >= 60 ? whole + 1 : whole) * 100;
  }

  /** compare-at × (1 − tier), rounded once. Never chained onto an already-discounted price. */
  function unitPriceAt(compareCents, tier) {
    const compare = Math.max(0, Math.floor(Number(compareCents) || 0));
    const pct = Math.min(100, Math.max(0, Number(tier) || 0));
    return roundUnit(Math.floor((compare * (100 - pct)) / 100));
  }

  /** The product's own subscription percentage, on the same .6 rounding rule. */
  function subPct(compareCents, subCents) {
    const compare = Number(compareCents) || 0;
    const sub = Number(subCents) || 0;
    if (compare <= 0 || sub <= 0 || sub >= compare) return 0;
    const pctX100 = Math.floor(((compare - sub) * 10000) / compare);
    const whole = Math.floor(pctX100 / 100);
    return pctX100 - whole * 100 >= 60 ? whole + 1 : whole;
  }

  /**
   * Whole-unit money in the shop's money_format (passed as data-money-format), no decimals
   * ever: `{{amount}}` and its variants all print the rounded whole number, so `$34`, `34 €`
   * or `kr 34` follow the market. Falls back to Intl when the format string is missing.
   */
  function money(cents, host) {
    const value = roundUnit(cents);
    const whole = String(Math.round(value / 100));
    const format = host && host.moneyFormat;
    if (format && /\{\{\s*amount/.test(format)) {
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const groupedDot = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      const groupedSpace = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return format
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, groupedDot)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, groupedDot)
        .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, groupedSpace)
        .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, groupedSpace)
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'"))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, grouped)
        .replace(/\{\{\s*amount\s*\}\}/g, grouped)
        .replace(/<[^>]*>/g, '')
        .trim();
    }
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (host && host.currency) || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value / 100);
  }

  function fill(template, values) {
    return Object.keys(values).reduce(
      (out, key) => out.split(`[${key}]`).join(String(values[key])),
      String(template || '')
    );
  }

  /**
   * Analytics. Converge is the store's event pipeline (window.cvg, consent-gated in
   * theme.liquid); a DOM event is dispatched too so any other listener can pick it up.
   */
  function track(eventName, properties) {
    try {
      if (typeof window.cvg === 'function') {
        window.cvg({ method: 'track', eventName, properties });
      }
      document.dispatchEvent(new CustomEvent('byob:' + eventName, { bubbles: true, detail: properties }));
    } catch (error) {
      /* analytics must never break the builder */
    }
  }

  class ByobBuilder extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.strings = this._readStrings();
      this.maxUnits = Math.max(1, parseInt(this.dataset.maxUnits, 10) || 6);
      this.maxPerProduct = Math.max(1, parseInt(this.dataset.maxPerProduct, 10) || 2);
      this.subscribeEnabled = this.dataset.subscribeEnabled === 'true';
      this.subscribeOn = this.subscribeEnabled && this.dataset.subscribeDefault === 'true';
      this.subPctMin = parseInt(this.dataset.subPctMin, 10) || 0;
      this.subPctMax = parseInt(this.dataset.subPctMax, 10) || 0;
      this.market = this.dataset.market || '';
      this.currency = this.dataset.currency || '';
      this.moneyFormat = this.dataset.moneyFormat || '';

      this.otpLadder = this._parseLadder(this.dataset.otpLadder, '0,10,15,20,25,30');
      this.subLadder = this._parseLadder(this.dataset.subLadder, 'auto,35,40,45,50,55');

      this.products = this._readProducts();
      this.selection = new Map();

      this.panel = this.querySelector('[data-byob-panel]');
      this.head = this.querySelector('[data-byob-head]');
      this.linesEl = this.querySelector('[data-byob-lines]');
      this.emptySlot = this.querySelector('[data-byob-empty-slot]');
      this.submitBtn = this.querySelector('[data-byob-submit]');
      this.errorEl = this.querySelector('[data-byob-error]');

      this.desktop = window.matchMedia('(min-width: 992px)');
      // Closed by default on mobile (scope). Desktop has no collapsed state.
      this.panelOpen = false;

      this._bind();
      this._applyBreakpoint();
      this._reserveSpace();
      this.render();
    }

    _readStrings() {
      const node = this.querySelector('[data-byob-strings]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('byob: could not parse the string table', error);
        return {};
      }
    }

    /** `auto` (or an empty token, which is how JSON null joins) becomes null: the product's own price. */
    _parseLadder(raw, fallback) {
      const source = String(raw || '').trim() === '' ? fallback : raw;
      return String(source)
        .split(',')
        .map((entry) => {
          const token = entry.trim().toLowerCase();
          if (token === 'auto' || token === '' || token === 'null') return null;
          const value = parseInt(token, 10);
          return Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
        });
    }

    _readProducts() {
      return [...this.querySelectorAll('[data-byob-card]')].map((el) => ({
        el,
        id: el.dataset.productId,
        handle: el.dataset.handle || '',
        variantId: Number(el.dataset.variantId),
        title: el.dataset.title || '',
        compare: parseInt(el.dataset.compare, 10) || 0,
        sub: parseInt(el.dataset.sub, 10) || 0,
        plan: (el.dataset.plan || '').trim(),
        thumb: el.dataset.thumb || '',
        inStock: el.dataset.available !== 'false',
        max: Math.max(1, parseInt(el.dataset.max, 10) || this.maxPerProduct),
      }));
    }

    _bind() {
      this.addEventListener('click', (event) => {
        const card = event.target.closest('[data-byob-card]');
        if (card) {
          if (event.target.closest('[data-byob-add]') || event.target.closest('[data-byob-inc]')) {
            return this.add(card.dataset.productId, 'grid');
          }
          if (event.target.closest('[data-byob-dec]')) {
            return this.remove(card.dataset.productId, 'grid');
          }
        }

        const lineStep = event.target.closest('[data-byob-line-inc], [data-byob-line-dec]');
        if (lineStep) {
          const id = lineStep.closest('[data-byob-line]').dataset.productId;
          return lineStep.hasAttribute('data-byob-line-inc') ? this.add(id, 'drawer') : this.remove(id, 'drawer');
        }

        if (event.target.closest('[data-byob-subscribe]')) return this.toggleSubscribe();
        if (event.target.closest('[data-byob-head]')) return this.togglePanel();
        if (event.target.closest('[data-byob-scrim]')) return this.setPanelOpen(false);
        if (event.target.closest('[data-byob-submit]')) this.submit();
      });

      // No scrim and no scroll lock: the scope wants the shopper still able to browse the
      // grid behind the open drawer ("the customer needs to feel they can keep shopping").
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.panelOpen && !this.desktop.matches) {
          this.setPanelOpen(false);
          if (this.head) this.head.focus();
        }
      });

      const onBreakpoint = () => {
        this._applyBreakpoint();
        this._reserveSpace();
        this.render();
      };
      if (typeof this.desktop.addEventListener === 'function') this.desktop.addEventListener('change', onBreakpoint);
      else this.desktop.addListener(onBreakpoint);

      if ('ResizeObserver' in window && this.panel) {
        this._panelObserver = new ResizeObserver(() => this._reserveSpace());
        this._panelObserver.observe(this.panel);
      }

      this._watchFooterLocalization();
    }

    /**
     * Mobile: the fixed drawer covers the footer's country/language selector, so it slides
     * away while that form is in view and comes back as soon as it leaves. IntersectionObserver,
     * not a scroll listener (theme rule). Desktop is untouched — the sidebar is not fixed.
     */
    _watchFooterLocalization() {
      if (!('IntersectionObserver' in window) || !this.panel) return;
      // Country + language selectors (footer: #localization_form_footer, others: #localization_form).
      const forms = [...document.querySelectorAll('form.shopify-localization-form, #localization_form, #localization_form_footer')];
      if (!forms.length) return;
      const visible = new Set();
      this._footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target)));
          this.panel.classList.toggle('is-yielding', visible.size > 0 && !this.desktop.matches);
        },
        { threshold: 0 }
      );
      forms.forEach((form) => this._footerObserver.observe(form));
    }

    /** The header is a real control only on mobile; on desktop the sidebar cannot collapse. */
    _applyBreakpoint() {
      if (!this.head) return;
      if (this.desktop.matches) {
        this.head.removeAttribute('aria-expanded');
        this.head.setAttribute('aria-disabled', 'true');
        this.head.setAttribute('tabindex', '-1');
      } else {
        this.head.removeAttribute('aria-disabled');
        this.head.removeAttribute('tabindex');
        this.head.setAttribute('aria-expanded', String(this.panelOpen));
      }
    }

    /**
     * The sheet's chrome — header, pills, subscribe box, totals, CTA — is content-sized and never
     * scrolls; only `.byob-lines` does. Published as `--byob-chrome-h` so the sheet's max-height
     * can reserve room for it (assets/byob.css, .byob-panel__sheet): at the design height of 74dvh
     * the chrome alone is 462px, so on a short dynamic viewport `.byob-panel__body-inner` used to
     * pick up a scrollbar of its own and the sheet had two. Measured rather than hardcoded because
     * the chrome moves with the subscribe toggle, the perk count and translated copy that wraps.
     */
    _measureChrome() {
      const sheet = this.panel && this.panel.querySelector('.byob-panel__sheet');
      const inner = this.panel && this.panel.querySelector('.byob-panel__body-inner');
      const body = this.querySelector('[data-byob-body]');
      if (!sheet || !inner || !body || !this.linesEl) return;

      const px = (el, prop) => parseFloat(getComputedStyle(el).getPropertyValue(prop)) || 0;
      const shown = (el) => el.getClientRects().length > 0;

      // Sheet: its padding, the gaps between its visible children, and every child but the body.
      const sheetKids = [...sheet.children].filter(shown);
      let chrome =
        px(sheet, 'padding-top') +
        px(sheet, 'padding-bottom') +
        px(sheet, 'row-gap') * Math.max(0, sheetKids.length - 1) +
        px(body, 'margin-block-start'); // negative: header → pills is a 10px gap, not 14
      sheetKids.forEach((el) => {
        if (el !== body) chrome += el.offsetHeight;
      });

      // Body: the gaps around the list, and every sibling of the list.
      const innerKids = [...inner.children].filter(shown);
      chrome += px(inner, 'row-gap') * Math.max(0, innerKids.length - 1);
      innerKids.forEach((el) => {
        if (el !== this.linesEl) chrome += el.offsetHeight;
      });

      chrome = Math.ceil(chrome);
      // Write only on a change: this property drives the sheet's max-height, which resizes the
      // panel, which is what the ResizeObserver watches. Rewriting it unconditionally would loop.
      if (chrome === this._chromeH) return;
      this._chromeH = chrome;
      this.style.setProperty('--byob-chrome-h', `${chrome}px`);
    }

    /** The fixed drawer must never cover the last grid row: reserve its height below the grid. */
    _reserveSpace() {
      if (!this.panel) return;
      this._measureChrome();
      if (this.desktop.matches) {
        this.style.removeProperty('--byob-drawer-h');
        return;
      }
      this.style.setProperty('--byob-drawer-h', `${Math.ceil(this.panel.getBoundingClientRect().height)}px`);
    }

    /**
     * Sellable right now. A product with no matching selling plan cannot go into a
     * subscription bundle — adding it without one would silently sell a one-time line
     * inside a subscription order.
     */
    canBuy(product) {
      if (!product.inStock) return false;
      return !(this.subscribeOn && !product.plan);
    }

    /** Tier is driven by TOTAL units, not distinct products. */
    units() {
      let total = 0;
      this.selection.forEach((qty) => {
        total += qty;
      });
      return total;
    }

    ladder() {
      return this.subscribeOn ? this.subLadder : this.otpLadder;
    }

    /** null means `auto`: each product keeps its own subscription price (1 unit is not a bundle). */
    tierFor(n) {
      if (n < 1) return 0;
      const ladder = this.ladder();
      const entry = ladder[Math.min(n, ladder.length) - 1];
      return entry === null || entry === undefined ? null : entry || 0;
    }

    /** Next rung that improves on now. Skips the 0% rung, so it never reads "add 1 more to save 0%". */
    nextTier() {
      const n = this.units();
      if (n >= this.maxUnits) return null;

      const ladder = this.ladder();
      for (let i = Math.max(n + 1, 1); i <= this.maxUnits; i += 1) {
        const entry = ladder[Math.min(i, ladder.length) - 1];
        if (entry === null || entry === undefined) {
          if (n === 0 && this.subPctMin > 0) return { addMore: 1, atUnits: 1, pct: this.subPctMin };
          continue;
        }
        if (entry > 0) return { addMore: i - n, atUnits: i, pct: entry };
      }
      return null;
    }

    unitPrice(product) {
      const tier = this.tierFor(this.units() || 1);
      if (tier === null) return roundUnit(product.sub > 0 ? product.sub : product.compare);
      return unitPriceAt(product.compare, tier);
    }

    /** Always the ladder's number — never one back-computed from the rounded dollars. */
    shownPct() {
      const n = this.units();
      if (n === 0) return 0;
      const tier = this.tierFor(n);
      if (tier !== null) return tier;
      const first = this.selected()[0];
      return first ? subPct(first.compare, first.sub) : this.subPctMin;
    }

    selected() {
      return this.products.filter((product) => this.selection.get(product.id) > 0);
    }

    _eventBase() {
      return {
        units: this.units(),
        tier_percent: this.shownPct(),
        purchase_type: this.subscribeOn ? 'subscription' : 'onetime',
        market: this.market,
        currency: this.currency,
      };
    }

    add(productId, source) {
      const product = this.products.find((item) => item.id === productId);
      if (!product || !this.canBuy(product)) return;
      const current = this.selection.get(productId) || 0;

      if (current >= product.max) return this._flashLimit(product, fill(this.strings.limitProduct, { max: product.max }));
      if (this.units() >= this.maxUnits) return this._flashLimit(product, fill(this.strings.limitBundle, { max: this.maxUnits }));

      this.selection.set(productId, current + 1);
      this.render();
      track('byob_product_added', { ...this._eventBase(), product_id: product.id, product_handle: product.handle, quantity: current + 1, source });
    }

    remove(productId, source) {
      const current = this.selection.get(productId) || 0;
      if (!current) return;
      if (current === 1) this.selection.delete(productId);
      else this.selection.set(productId, current - 1);
      this.render();
      const product = this.products.find((item) => item.id === productId);
      track('byob_product_removed', { ...this._eventBase(), product_id: productId, product_handle: product ? product.handle : '', quantity: current - 1, source });
    }

    /** "Limit is 2 per product" / "Bundle is full" — shown on the card, cleared on the next change. */
    _flashLimit(product, message) {
      const el = product.el.querySelector('[data-byob-card-limit]');
      if (!el || !message) return;
      // One message at a time: a second tap on another card must not leave the first one stuck.
      this.querySelectorAll('[data-byob-card-limit]').forEach((node) => {
        if (node !== el) node.hidden = true;
      });
      el.textContent = message;
      el.hidden = false;
      clearTimeout(this._limitTimer);
      this._limitTimer = setTimeout(() => {
        el.hidden = true;
      }, 2400);
    }

    /** Flipping to subscription drops anything that has no plan, so the cart stays honest. */
    toggleSubscribe() {
      if (!this.subscribeEnabled) return;
      this.subscribeOn = !this.subscribeOn;
      this.products.forEach((product) => {
        if (!this.canBuy(product)) this.selection.delete(product.id);
      });
      this.render();
      track(this.subscribeOn ? 'byob_subscribe_on' : 'byob_subscribe_off', this._eventBase());
    }

    togglePanel() {
      if (this.desktop.matches) return;
      this.setPanelOpen(!this.panelOpen);
      track(this.panelOpen ? 'byob_drawer_opened' : 'byob_drawer_closed', this._eventBase());
    }

    setPanelOpen(open) {
      this.panelOpen = Boolean(open);
      if (this.panel) this.panel.setAttribute('data-open', String(this.panelOpen));
      this._applyBreakpoint();
      this.renderPanelChrome();
      this._reserveSpace();
      if (this.panelOpen) this._scrollLinesToEnd();
    }

    /**
     * The drawer opens with the list scrolled to its end, so the newest row and the empty slot
     * are what the customer sees, not the first product they picked. Set now, while the body is
     * still at 0fr (the value clamps as the row grows, so it stays pinned to the end), and again
     * when the open transition lands, in case a browser re-clamps mid-animation.
     */
    _scrollLinesToEnd() {
      const list = this.linesEl;
      if (!list) return;
      const pin = () => {
        list.scrollTop = list.scrollHeight;
      };
      pin();
      const body = this.querySelector('[data-byob-body]');
      if (!body) return;
      if (this._pinOnOpenEnd) body.removeEventListener('transitionend', this._pinOnOpenEnd);
      this._pinOnOpenEnd = (event) => {
        // Children's own transitions bubble here too; only the body's row growth counts.
        if (event.target !== body || event.propertyName !== 'grid-template-rows') return;
        body.removeEventListener('transitionend', this._pinOnOpenEnd);
        this._pinOnOpenEnd = null;
        pin();
      };
      body.addEventListener('transitionend', this._pinOnOpenEnd);
    }

    render() {
      this.renderGrid();
      this.renderPills();
      this.renderSubscribe();
      this.renderLines();
      this.renderTotals();
      this.renderPanelChrome();
      this._measureChrome();
    }

    renderGrid() {
      const full = this.units() >= this.maxUnits;

      this.products.forEach((product) => {
        const qty = this.selection.get(product.id) || 0;
        const el = product.el;
        const buyable = this.canBuy(product);

        el.classList.toggle('is-selected', qty > 0);
        el.classList.toggle('byob-card--unavailable', !buyable && product.inStock);
        // Bundle full: every card NOT in the bundle greys out as a whole (selected cards stay
        // live so their "−" still works). Tapping a greyed card still explains why.
        el.classList.toggle('byob-card--blocked', full && qty === 0 && buyable);

        const addBtn = el.querySelector('[data-byob-add]');
        const stepper = el.querySelector('[data-byob-stepper]');
        if (addBtn) {
          addBtn.hidden = qty > 0;
          // Stays clickable when the bundle is full so the tap can explain why (scope: show a
          // message saying the bundle is full); aria-disabled carries the state.
          addBtn.disabled = !buyable;
          addBtn.setAttribute('aria-disabled', String(!buyable || full));
          addBtn.classList.toggle('is-blocked', full && buyable);
          if (!buyable && product.inStock && this.strings.noPlan) addBtn.setAttribute('title', this.strings.noPlan);
          else addBtn.removeAttribute('title');
        }
        if (stepper) stepper.hidden = qty === 0;

        const qtyEl = el.querySelector('[data-byob-qty]');
        if (qtyEl) qtyEl.textContent = String(qty);

        const inc = el.querySelector('[data-byob-inc]');
        if (inc) {
          const blocked = full || qty >= product.max;
          inc.setAttribute('aria-disabled', String(blocked));
          inc.classList.toggle('is-blocked', blocked);
        }

        if (!product.inStock) return;

        const price = this.unitPrice(product);
        const compare = roundUnit(product.compare);
        const priceEl = el.querySelector('[data-byob-card-price]');
        const compareEl = el.querySelector('[data-byob-card-compare]');
        const onSale = price < compare;
        if (priceEl) priceEl.textContent = money(price, this);
        if (compareEl) {
          compareEl.textContent = money(compare, this);
          compareEl.hidden = !onSale;
        }
      });
    }

    renderPills() {
      const n = this.units();
      const ladder = this.ladder();

      this.querySelectorAll('[data-byob-pill]').forEach((pill, index) => {
        const step = index + 1;
        const entry = ladder[Math.min(step, ladder.length) - 1];
        pill.textContent = entry === null || entry === undefined ? this._autoLabel() : `${entry}%`;
        pill.classList.toggle('is-active', n === step);
        pill.classList.toggle('is-reached', n > step);
      });
    }

    _autoLabel() {
      if (!this.subPctMin) return '—';
      if (this.subPctMax > this.subPctMin) return `${this.subPctMin}-${this.subPctMax}%`;
      return `${this.subPctMin}%`;
    }

    renderSubscribe() {
      const toggle = this.querySelector('[data-byob-subscribe]');
      if (!toggle) return;
      toggle.setAttribute('aria-checked', String(this.subscribeOn));
      const sub = this.querySelector('[data-byob-sub]');
      if (sub) sub.classList.toggle('is-off', !this.subscribeOn);
    }

    renderLines() {
      if (!this.linesEl) return;
      this.linesEl.querySelectorAll('[data-byob-line]').forEach((node) => node.remove());

      const full = this.units() >= this.maxUnits;
      const fragment = document.createDocumentFragment();

      this.selected().forEach((product) => {
        const qty = this.selection.get(product.id);
        const each = this.unitPrice(product);
        const compareEach = roundUnit(product.compare);

        const row = document.createElement('div');
        row.className = 'byob-line';
        row.setAttribute('data-byob-line', '');
        row.dataset.productId = product.id;

        const thumb = product.thumb
          ? `<span class="byob-line__thumb"><img src="${product.thumb}" alt="" width="50" height="50" loading="lazy"></span>`
          : '<span class="byob-line__thumb" aria-hidden="true"></span>';

        row.innerHTML = `
          ${thumb}
          <span class="byob-line__info"><b class="byob-line__name"></b></span>
          <span class="byob-line__controls">
            <span class="byob-line__stepper">
              <button type="button" class="byob-line__step" data-byob-line-dec>${MINUS_SVG}</button>
              <span class="byob-line__qty"></span>
              <button type="button" class="byob-line__step" data-byob-line-inc>${PLUS_SVG}</button>
            </span>
            <span class="byob-line__price">
              <span class="byob-line__price-now"></span>
              <s class="byob-line__price-was"></s>
            </span>
          </span>`;

        // textContent, not interpolation: a product title is merchant data.
        row.querySelector('.byob-line__name').textContent = product.title;
        row.querySelector('.byob-line__qty').textContent = String(qty);
        const now = row.querySelector('.byob-line__price-now');
        now.textContent = money(each * qty, this);
        now.classList.toggle('byob-line__price-now--sale', each < compareEach);

        const was = row.querySelector('.byob-line__price-was');
        if (each < compareEach) was.textContent = money(compareEach * qty, this);
        else was.remove();

        row.querySelector('[data-byob-line-dec]').setAttribute('aria-label', `${this.strings.removeOne} ${product.title}`);
        const inc = row.querySelector('[data-byob-line-inc]');
        inc.setAttribute('aria-label', `${this.strings.addOne} ${product.title}`);
        const blocked = full || qty >= product.max;
        inc.setAttribute('aria-disabled', String(blocked));
        inc.classList.toggle('is-blocked', blocked);

        fragment.appendChild(row);
      });

      this.linesEl.insertBefore(fragment, this.emptySlot || null);

      const next = this.nextTier();
      if (this.emptySlot) {
        this.emptySlot.hidden = !next;
        const hint = this.emptySlot.querySelector('[data-byob-empty-hint]');
        if (hint && next) hint.textContent = fill(this.strings.unlockAt, { count: next.atUnits, percent: next.pct });
      }
    }

    renderTotals() {
      const totals = this.querySelector('[data-byob-totals]');
      if (!totals) return;

      // Empty state: no breakdown, no zeros.
      if (this.units() === 0) {
        totals.hidden = true;
        this._payTotal = 0;
        return;
      }

      let compareTotal = 0;
      let payTotal = 0;
      this.selected().forEach((product) => {
        const qty = this.selection.get(product.id);
        compareTotal += roundUnit(product.compare) * qty;
        payTotal += this.unitPrice(product) * qty;
      });

      totals.hidden = false;
      this.querySelector('[data-byob-total-value]').textContent = money(compareTotal, this);
      this.querySelector('[data-byob-discount-label]').textContent = fill(this.strings.bundleDiscount, { percent: this.shownPct() });
      this.querySelector('[data-byob-total-discount]').textContent = `-${money(compareTotal - payTotal, this)}`;
      this.querySelector('[data-byob-total-pay]').textContent = money(payTotal, this);

      this._payTotal = payTotal;
    }

    renderPanelChrome() {
      const n = this.units();

      const count = this.querySelector('[data-byob-count]');
      if (count) count.textContent = fill(this.strings.count, { count: n, max: this.maxUnits });

      if (this.submitBtn) this.submitBtn.disabled = n === 0 || Boolean(this._submitting);

      // The price rides on the button ONLY when the Total row is hidden (mobile, collapsed).
      const label = this.querySelector('[data-byob-cta-label]');
      const collapsed = !this.desktop.matches && !this.panelOpen;
      if (label) {
        const text = collapsed && n > 0 ? `${this.strings.ctaLabel} · ${money(this._payTotal || 0, this)}` : this.strings.ctaLabel;
        if (label.textContent.trim() !== text) label.textContent = text;
      }

      this._renderTag();
    }

    /**
     * Green tag, mobile collapsed state only (CSS hides it open + desktop). Two parts: what is
     * unlocked carries the weight, the nudge sits lighter beside it, "·" as the separator.
     */
    _renderTag() {
      const tag = this.querySelector('[data-byob-tag]');
      const text = tag && tag.querySelector('[data-byob-tag-text]');
      if (!tag || !text) return;

      const pct = this.shownPct();
      const next = this.nextTier();
      text.textContent = '';

      tag.classList.toggle('is-nudge-only', !(pct > 0));
      if (!next && pct > 0) {
        tag.classList.add('is-max');
        text.append(this.strings.tagMax);
        const capped = document.createElement('em');
        capped.textContent = ` · ${fill(this.strings.tagMaxPct, { percent: pct })}`;
        text.appendChild(capped);
      } else if (next && pct > 0) {
        tag.classList.remove('is-max');
        text.append(fill(this.strings.tagUnlocked, { percent: pct }));
        const nudge = document.createElement('em');
        nudge.textContent = ` · ${fill(this.strings.tagNext, { count: next.addMore, percent: next.pct })}`;
        text.appendChild(nudge);
      } else if (next) {
        tag.classList.remove('is-max');
        text.append(fill(this.strings.tagSave, { count: next.addMore, percent: next.pct }));
      }

      tag.toggleAttribute('data-empty', !text.textContent);
    }

    async submit() {
      if (!this.units() || this._submitting) return;

      const bundleId = `byob-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const totalUnits = this.units();
      const tier = this.shownPct();
      const idProperty = this.dataset.bundleIdProperty || '_byob_bundle_id';
      const markerProperty = this.dataset.bundleMarkerProperty || 'Bundle';
      const markerValue = this.dataset.bundleMarkerValue || 'Build Your Own Bundle';

      // Only `_byob_bundle_id` (grouping) and the visible marker are read by the Function.
      // units / tier / type are hints for support and analytics; the Function recounts them.
      const items = this.selected().map((product) => {
        const properties = {
          [markerProperty]: markerValue,
          [idProperty]: bundleId,
          _byob_units: String(totalUnits),
          _byob_tier: String(tier),
          _byob_type: this.subscribeOn ? 'subscription' : 'onetime',
        };
        const line = { id: product.variantId, quantity: this.selection.get(product.id), properties };
        if (this.subscribeOn && product.plan) line.selling_plan = Number(product.plan) || product.plan;
        return line;
      });

      this._setSubmitting(true);
      this._setError('');

      const routes = (window.themeVariables && window.themeVariables.routes) || {};
      const addUrl = `${(routes.cartAddUrl || '/cart/add').replace(/\.js$/, '')}.js`;
      const cartUrl = `${(routes.cartUrl || '/cart').replace(/\.js$/, '')}.js`;

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: ['mini-cart'], sections_url: window.location.pathname }),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          this._setError(result.description || result.message || this.strings.cartError);
          return;
        }

        track('byob_bundle_added', {
          ...this._eventBase(),
          bundle_id: bundleId,
          units: totalUnits,
          tier_percent: tier,
          total_cents: this._payTotal || 0,
          items: items.map((line) => ({ variant_id: line.id, quantity: line.quantity })),
        });

        // Same handshake as bundle-buy-box.js: full cart + the freshly rendered mini-cart
        // section, then the theme's own drawer opens itself on `openMiniCart`.
        let cartContent = null;
        try {
          cartContent = await (await fetch(cartUrl, { headers: { Accept: 'application/json' } })).json();
        } catch (error) {
          cartContent = null;
        }
        if (cartContent && result.sections) cartContent.sections = result.sections;

        this.selection.clear();
        this.setPanelOpen(false);
        this.render();

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { cart: cartContent || undefined, openMiniCart: true },
          })
        );
        const drawer = document.getElementById('mini-cart');
        if (drawer && !cartContent) {
          drawer.open = true;
          drawer.setAttribute('open', '');
        }
      } catch (error) {
        console.error('byob: add to cart failed', error);
        this._setError(this.strings.cartError);
      } finally {
        this._setSubmitting(false);
      }
    }

    _setSubmitting(state) {
      this._submitting = state;
      if (!this.submitBtn) return;
      this.submitBtn.classList.toggle('is-loading', state);
      this.submitBtn.disabled = state || this.units() === 0;
    }

    _setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }
  }

  customElements.define('byob-builder', ByobBuilder);
}
